/**
 * Configuración del Portal Condominio: la ficha del proyecto (`proyectos`) y el resumen de cuotas
 * y cobro que sale de las unidades reales.
 */
import { supabase } from "@/integrations/supabase/client";
import { centavos, type TendenciaCondominio, type UnidadCondominioRpc } from "./unidades";

const sb = supabase as any;

export interface CondominioConfig {
  nombre: string;
  direccion: string | null;
  logo: string | null;
  portada: string | null;
  firmante_recibos: string | null;
  fecha_entrega: string | null;
  costo_mantenimiento_m2: number;
  monto_mensual_cuota_extraordinaria: number;
}

export async function fetchCondominioConfig(proyectoId: number): Promise<CondominioConfig> {
  const { data, error } = await sb
    .from("proyectos")
    .select("nombre, direccion, url_logo, url_imagen_portada, nombre_firmante_recibos, fecha_entrega_proyecto, fecha_entrega, costo_mantenimiento_m2, monto_mensual_cuota_extraordinaria")
    .eq("id", proyectoId)
    .single();
  if (error) throw error;
  const t = (v: unknown) => (typeof v === "string" && v.trim() !== "" ? v.trim() : null);
  return {
    nombre: t(data?.nombre) ?? "Sin nombre",
    direccion: t(data?.direccion),
    logo: t(data?.url_logo),
    portada: t(data?.url_imagen_portada),
    firmante_recibos: t(data?.nombre_firmante_recibos),
    fecha_entrega: data?.fecha_entrega_proyecto || data?.fecha_entrega
      ? String(data.fecha_entrega_proyecto ?? data.fecha_entrega).slice(0, 10)
      : null,
    costo_mantenimiento_m2: Number(data?.costo_mantenimiento_m2) || 0,
    monto_mensual_cuota_extraordinaria: Number(data?.monto_mensual_cuota_extraordinaria) || 0,
  };
}

export interface ResumenCuotas {
  unidades: number;
  m2: number;
  cuotaMensual: number;
  cuotaPromedio: number;
  /** Fondo de reserva como % del mantenimiento, en el último mes con cuotas; null si no hay. */
  fondoPct: number | null;
  mttoMes: number;
  fondoMes: number;
  /** Mes (YYYY-MM-01) del que salen mantenimiento y fondo. */
  mesReferencia: string | null;
  conClabe: number;
  conTitular: number;
  alCorriente: number;
  /** Unidad típica (mediana de m²) para explicar el cálculo de la cuota. */
  ejemplo: { unidad: string; m2: number; cuota: number } | null;
}

/** Resumen de cuotas y cobro con las unidades reales y lo esperado del último mes con cuotas. */
export function resumenCuotas(unidades: UnidadCondominioRpc[], meses: TendenciaCondominio[]): ResumenCuotas {
  let m2 = 0;
  let cuota = 0;
  for (const u of unidades) {
    m2 += u.area_m2 || 0;
    cuota += centavos(u.cuota_mensual);
  }
  const mes = [...meses].reverse().find((m) => centavos(m.mtto_esperado) > 0) ?? null;
  const mtto = mes ? centavos(mes.mtto_esperado) : 0;
  const fondo = mes ? centavos(mes.fondo_esperado) : 0;
  const conM2 = unidades.filter((u) => u.area_m2 > 0 && u.cuota_mensual > 0).sort((a, b) => a.area_m2 - b.area_m2);
  const med = conM2.length ? conM2[Math.floor(conM2.length / 2)] : null;
  return {
    unidades: unidades.length,
    m2: Math.round(m2 * 100) / 100,
    cuotaMensual: cuota / 100,
    cuotaPromedio: unidades.length ? Math.round(cuota / unidades.length) / 100 : 0,
    fondoPct: mtto > 0 ? Math.round((fondo / mtto) * 1000) / 10 : null,
    mttoMes: mtto / 100,
    fondoMes: fondo / 100,
    mesReferencia: mes?.mes ?? null,
    conClabe: unidades.filter((u) => !!u.clabe).length,
    conTitular: unidades.filter((u) => !!u.propietario).length,
    alCorriente: unidades.filter((u) => u.dias_vencido === 0).length,
    ejemplo: med ? { unidad: med.unidad, m2: med.area_m2, cuota: med.cuota_mensual } : null,
  };
}
